import { Experiment } from "@amplitude/experiment-js-client"
import { type PropsWithChildren, Suspense, useEffect } from "react"
import { z } from "zod"

import { env } from "../env"
import { getRepositoryName } from "../routing"
import { trackExperimentExposure } from "../services/tracking"
import { useProfile } from "../services/user"

const experiment = Experiment.initialize(
  env.PAGE_BUILDER_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY,
  {
    fetchOnStart: true,
    serverUrl: new URL("api", env.AMPLITUDE_EXPERIMENT_SERVER_BASE_URL).href,
    flagsServerUrl: new URL("flag", env.AMPLITUDE_EXPERIMENT_SERVER_BASE_URL)
      .href,
    exposureTrackingProvider: {
      track: trackExperimentExposure
    }
  }
)

export function AmplitudeExperiments(props: PropsWithChildren) {
  return (
    <>
      <Suspense>
        <BlockingExperiments />
      </Suspense>
      {props.children}
    </>
  )
}

function BlockingExperiments() {
  const { shortId } = useProfile()
  const repositoryName = getRepositoryName()

  useEffect(() => {
    experiment
      .start({
        user_id: shortId,
        groups: {
          Repository: [repositoryName]
        }
      })
      .catch(console.error)
  }, [shortId, repositoryName])

  return null
}

export function clearExperiments() {
  experiment.stop()
  experiment.clear()
}

export function promptToSliceFlag() {
  const variant = experiment.variant("prompt-to-slice")
  return z.enum(["on", "off"]).catch("off").parse(variant.value)
}

export function aiTranslationFlag() {
  const variant = experiment.variant("ai-translation")
  return z.enum(["on", "off"]).catch("off").parse(variant.value)
}

export function documentListFlag() {
  // If running the app locally, hardcode the flag to "on" as we want to show the new doc list to developers working on it.
  if (import.meta.env.DEV) return "on"

  const variant = experiment.variant("pb-document-list")
  return z.enum(["on", "off"]).catch("off").parse(variant.value)
}

export function releasesTabFlag() {
  const variant = experiment.variant("pb-releases-tab")
  return z.enum(["on", "off"]).catch("off").parse(variant.value)
}

export function migrationReleaseNoModal() {
  const variant = experiment.variant("migration-release-no-modal")
  return z.enum(["on", "off"]).catch("off").parse(variant.value)
}

export function sharedOnboardingGuide() {
  const variant = experiment.variant("slicemachine-shared-onboarding")
  return z
    .enum(["control", "off", "with-shared-onboarding"])
    .catch("off")
    .parse(variant.value)
}
